import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useStripe } from '../contexts/StripeContext';
import { useCredits } from '../contexts/CreditContext';
import { CREDIT_PACKAGES, formatPrice } from '../lib/stripe';
import { trackCreditPurchase } from '../lib/analytics';
import { StripeCheckout } from './StripeCheckout';

interface CreditPurchaseFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const CreditPurchaseForm: React.FC<CreditPurchaseFormProps> = ({
  onSuccess,
  onCancel,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clientSecret, createPaymentIntent, isLoading, error, setClientSecret } = useStripe();
  const { refreshCredits } = useCredits();
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);

  const handlePackageSelect = (packageId: string) => {
    setSelectedPackage(packageId);
  };

  const handlePurchase = async () => {
    if (!selectedPackage) return;

    const pkg = CREDIT_PACKAGES.find(p => p.id === selectedPackage);
    if (!pkg) return;

    await createPaymentIntent(pkg.price, pkg.id);
  };

  const handlePaymentSuccess = async () => {
    // Track credit purchase
    const pkg = CREDIT_PACKAGES.find(p => p.id === selectedPackage);
    if (pkg) {
      trackCreditPurchase(selectedPackage!, pkg.credits, pkg.price, 'BGN');
    }
    
    await refreshCredits();
    
    setClientSecret(null);
    setSelectedPackage(null);
    
    // Navigate to credits page
    navigate('/credits');
    onSuccess?.();
  };

  const handlePaymentCancel = () => {
    setClientSecret(null);
    onCancel?.();
  };



  // Show Stripe checkout when we have a clientSecret
  if (clientSecret) {
    return (
      <StripeCheckout
        onSuccess={handlePaymentSuccess}
        onCancel={handlePaymentCancel}
      />
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, textAlign: 'center', fontWeight: 600 }}>
        {t('credits.purchase.title')}
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 4, textAlign: 'center', color: 'text.secondary' }}>
        {t('credits.purchase.subtitle')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {CREDIT_PACKAGES.map((pkg) => (
          <Grid item xs={12} sm={6} md={3} key={pkg.id}>
            <Card
              sx={{
                height: '100%',
                cursor: 'pointer',
                border: selectedPackage === pkg.id ? '2px solid #10b981' : '1px solid #e5e7eb',
                transform: selectedPackage === pkg.id ? 'scale(1.02)' : 'scale(1)',
                transition: 'all 0.2s ease-in-out',
                position: 'relative',
                '&:hover': {
                  boxShadow: 3,
                },
              }}
              onClick={() => handlePackageSelect(pkg.id)}
            >
              {pkg.popular && (
                <Chip
                  label={t('credits.purchase.popular')}
                  color="primary"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    zIndex: 1,
                  }}
                />
              )}
              
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {pkg.name}
                </Typography>
                
                <Typography variant="h4" sx={{ color: '#10b981', fontWeight: 700, mb: 1 }}>
                  {formatPrice(pkg.price)}
                </Typography>
                
                <Typography variant="h6" sx={{ color: 'text.secondary', mb: 2 }}>
                  {pkg.credits} {t('credits.purchase.credits')}
                </Typography>
                
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                  {pkg.description}
                </Typography>
                
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('credits.purchase.pricePerCredit', { 
                    price: formatPrice(Math.round(pkg.price / pkg.credits))
                  })}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Button
          variant="contained"
          onClick={handlePurchase}
          disabled={!selectedPackage || isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
          size="large"
          sx={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            px: 6,
            py: 2,
            fontSize: '1.1rem',
            fontWeight: 600,
            boxShadow: '0 8px 20px rgba(16, 185, 129, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
              boxShadow: '0 12px 25px rgba(16, 185, 129, 0.4)',
            },
          }}
        >
          {isLoading ? t('credits.purchase.processing') : t('credits.purchase.buyNow')}
        </Button>
      </Box>

    </Box>
  );
};
